import React from 'react'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'
import { formatNumber } from '../../utils'

type StatsProps = {
    stats: Statistic[]
    justify?: string
    valueTextAlign?: string
    desktopShort?: boolean
    autoPadding?: boolean
    separatorColor?: string
    allSeparators?: boolean
    className?: string
}

type StatsType = 'number' | 'string' | 'date'

export type Statistic = {
    label: string
    type: StatsType
    value: number | string | Date | null
}

const Stats: React.FunctionComponent<StatsProps> = ({ stats, justify, desktopShort, valueTextAlign, autoPadding, separatorColor, allSeparators, className }) => {
    const items = stats.map((stat, i) => {
        const { label, type, value } = stat
        const format = chooseFormatFn(type)

        return (
            <>
            <Item key={i} justify={justify} valueTextAlign={valueTextAlign} autoPadding={autoPadding} isFirst={ i === 0}>
                <Label>{ label }</Label>
                <Stat>{ format(value) }</Stat>
            </Item>
            { i !== stats.length && (allSeparators || i % 3 !== 2) && <Separator color={separatorColor}/> }
            </>
        )
    })

    return (
        <Container desktopShort={desktopShort} className={className}>
            { items }
        </Container>
    )
}

const Container = styled.div<{ desktopShort?: boolean }>`
    display: grid;
    grid-template-columns: 1fr 1px 1fr 1px 1fr; // 3 + 2 separators
    justify-items: center;
    grid-gap: 16px;

    width: 100%;

    box-sizing: border-box;

    ${up('desktop')} {
        grid-gap: 24px;
        grid-template-columns: ${p => p.desktopShort ? 'auto 1px auto 1px auto' : '1fr 1px 1fr 1px 1fr' }; // 3 + 2 separators

        width: ${ p => p.desktopShort ? 'auto' : '100%' };
    }
`

const Stat = styled.div`
    font-weight: bold;
    font-size: 16px;
    line-height: 26px;

    text-align: center;
`

const Item = styled.div<{ justify?: string, valueTextAlign?: string, autoPadding?: boolean, isFirst: boolean }>`
    display: grid;
    grid-template-rows: auto auto;

    align-items: center;
    justify-items: center;

    ${up('desktop')} {
        justify-self: ${ p=> p.autoPadding ? 'center' : p.isFirst ? 'flex-start' : 'center' };

        justify-items: ${p => p.justify ? p.justify : 'center'};
        & > ${Stat} {
            text-align: ${p => p.valueTextAlign || 'left'};
        }

        padding-right: ${p => p.autoPadding ? 'auto' : '60px' };
        padding-left: ${p => p.autoPadding ? 'auto' : p.isFirst ? 0 : '60px'};
    }
`

const Separator = styled.div<{ color?: string }>`
    width: 1px;
    background: ${p => p.color || p.theme.colors.bg};
`

const Label = styled.div`
    font-weight: normal;
    font-size: 14px;
    line-height: 24px;
    opacity: 0.5;

    text-align: center;
`

const chooseFormatFn = (t: StatsType): ((x: any) => string) => {
    switch (t) {
        case 'number':
            return formatNumber
        case 'date':
            return formatDate
        case 'string':
            return (s: string | null) => s == null ? "—" : s
        default:
            console.log(`Unknown stat type for format: ${t}`)
            return (x: any) => x
    }
}

const formatDate = (d: Date): string => {
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d);
    const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(d);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);
    
    return `${da} ${mo} ${ye}`
}

export default Stats
